import { applySnapshot, cast, types } from "mobx-state-tree"
import { debounce } from "lodash"
import { DictionaryLink } from "modules/common/models/dictionary-link"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { BoardColumn, QuestionTag } from "./board"
import { QuestionCard } from "./card"

/**
 * Статус задания поверх колонок досок — как категория статуса в Jira. Один на все доски:
 * по нему же считаются бейджи заданий в списке сотрудников
 */
export const QuestionStatuses = {
  work: "work",
  review: "review",
  expired: "expired",
  done: "done",
}

export const STATUS_LABELS: TStringMap<string> = {
  [QuestionStatuses.work]: "В работе",
  [QuestionStatuses.review]: "На проверке",
  [QuestionStatuses.expired]: "Просрочено",
  [QuestionStatuses.done]: "Выполнено",
}

export const DUE_LABELS: TStringMap<string> = {
  "": "Любой срок",
  today: "Срок сегодня",
  week: "Срок на неделе",
  none: "Без срока",
}

export const SORT_LABELS: TStringMap<string> = {
  deadline: "По сроку",
  created: "По дате создания",
}

/**
 * Строка списка: карточка доски плюс статус и родитель. Идентификатор — identifier:
 * при перезагрузке списка узлы прежних строк сохраняются
 */
export const QuestionListRow = types
  .compose(
    QuestionCard,
    types.model({
      id: types.identifier,
      status: types.optional(types.string, ""),
      created: types.optional(types.string, ""),
      parentId: types.optional(types.string, ""),
      parentNumber: types.optional(types.string, ""),
      parentTitle: types.optional(types.string, ""),
    })
  )
  .named("QuestionListRow")

/** Доска пользователя: её колонки, метки и участники нужны карточке задания с этой доски */
export const QuestionListBoard = types
  .model({
    id: types.string,
    name: types.string,
    color: types.optional(types.string, ""),
    columns: types.array(BoardColumn),
    tags: types.array(QuestionTag),
    /** Владелец и участники личной доски. У общей доски — null */
    members: types.maybeNull(types.array(DictionaryLink)),
  })
  .named("QuestionListBoard")

const FILTERS_STORAGE_KEY = "questionsListFilters"

/** Что список запоминает между заходами: фильтры, но не сотрудника из ссылки и не поиск */
interface StoredFilters {
  due: string
  orderIds: string[]
  authorIds: string[]
  employerIds: string[]
  implementerIds: string[]
  sort: string
  desc: boolean
}

const readFilters = (): Partial<StoredFilters> => {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || "{}")
  } catch {
    return {}
  }
}

/**
 * Список заданий по всем доскам — как поиск задач в Jira: слева строки, справа карточка
 * выбранного задания. Фильтрация и сортировка на сервере, строки догружаются прокруткой
 */
export const QuestionList = types
  .compose(
    Notificator,
    Transport,
    types.model({
      items: types.array(QuestionListRow),
      total: types.optional(types.number, 0),
      counts: types.map(types.number),
      /** Сколько заданий в каждой колонке при остальных фильтрах: ключ «доска|колонка» */
      columnCounts: types.map(types.number),
      /** Сколько открытых заданий на каждой доске при остальных фильтрах: ключ — id доски */
      boardCounts: types.map(types.number),
      boards: types.array(QuestionListBoard),
      query: types.optional(types.string, ""),
      /**
       * Доска, с которой пришли в список: по ней идёт поиск, пока не включён переключатель
       * «По всем доскам». Пусто — общая доска заданий по проектам
       */
      homeBoard: types.optional(types.string, ""),
      /** Галочка «По всем доскам» над списком досок: искать по всем, выбор досок не важен */
      allBoards: types.optional(types.boolean, false),
      /** Выбранные доски. Пусто — доска, с которой пришли */
      boardIds: types.array(types.string),
      /** Статус из ссылки с бейджа сотрудника: показан плашкой, снимается крестиком */
      statuses: types.array(types.string),
      /**
       * Выбранные колонки досок, «доска|колонка». Колонки одной доски, при «По всем доскам» —
       * и других. Не запоминаются: у разных досок колонки свои
       */
      columns: types.array(types.string),
      due: types.optional(types.string, ""),
      orderIds: types.array(types.string),
      authorIds: types.array(types.string),
      employerIds: types.array(types.string),
      implementerIds: types.array(types.string),
      /** Сотрудник из ссылки со страницы «Сотрудники»: его задания, где он ответственный или исполнитель */
      employerId: types.optional(types.string, ""),
      /** Имя сотрудника из ссылки: показывается плашкой, которую можно снять */
      employerName: types.optional(types.string, ""),
      sort: types.optional(types.string, "deadline"),
      desc: types.optional(types.boolean, false),
      loading: types.optional(types.boolean, false),
      loadingMore: types.optional(types.boolean, false),
      /** Сначала загрузили — до этого «ничего не найдено» не показываем */
      loaded: types.optional(types.boolean, false),
    })
  )
  .views((self) => ({
    get hasMore() {
      return self.items.length < self.total
    },

    /** Доски, по которым идёт поиск: все, выбранные или та, с которой пришли */
    get selectedBoards(): string[] {
      if (self.allBoards) {
        return self.boards.map((b) => b.id)
      }

      return self.boardIds.length > 0 ? self.boardIds.slice() : [self.homeBoard]
    },

    board(id: string) {
      return self.boards.find((b) => b.id === (id || "")) || null
    },

    /** Колонка задания на его доске: название и цвет для строки списка */
    column(boardId: string, key: string) {
      const board = self.boards.find((b) => b.id === (boardId || ""))
      const column = board ? board.columns.find((c) => c.key === key) : null

      return column ? { name: column.name, color: column.color } : null
    },

    get params() {
      return {
        boardIds: self.allBoards ? [] : self.boardIds.length > 0 ? self.boardIds.slice() : [self.homeBoard],
        statuses: self.statuses.slice(),
        columns: self.columns.slice(),
        due: self.due,
        orderIds: self.orderIds.slice(),
        authorIds: self.authorIds.slice(),
        filterEmployerIds: self.employerIds.slice(),
        filterImplementerIds: self.implementerIds.slice(),
        employerId: self.employerId,
        complexSearch: self.query.trim(),
        sort: self.sort,
        desc: self.desc,
      }
    },
  }))
  .actions((self) => {
    /** Номер последнего запроса: ответ на устаревший запрос не затирает свежий */
    let requestNo = 0

    const store = () => {
      const value: StoredFilters = {
        due: self.due,
        orderIds: self.orderIds.slice(),
        authorIds: self.authorIds.slice(),
        employerIds: self.employerIds.slice(),
        implementerIds: self.implementerIds.slice(),
        sort: self.sort,
        desc: self.desc,
      }

      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(value))
      } catch {
        // браузер не даёт хранить — фильтры просто не запомнятся
      }
    }

    const load = flow(function* () {
      const no = ++requestNo
      self.loading = true

      try {
        const page: any = yield self.transport.post<any>(apiUrls.orders.orderQuestion.search, {
          ...self.params,
          skip: 0,
        })

        if (no !== requestNo) {
          return false
        }

        applySnapshot(self.items, page.items || [])
        applySnapshot(self.boards, page.boards || [])
        applySnapshot(self.counts, page.counts || {})
        applySnapshot(self.columnCounts, page.columnCounts || {})
        applySnapshot(self.boardCounts, page.boardCounts || {})
        self.total = page.total || 0
        self.loaded = true

        return true
      } catch (e) {
        no === requestNo && self.notify.error(e)
        return false
      } finally {
        no === requestNo && (self.loading = false)
      }
    })

    /** Колонки досок, по которым больше не ищем, из выбора убираем */
    const dropForeignColumns = () => {
      const boards = (self as unknown as { selectedBoards: string[] }).selectedBoards
      self.columns = cast(self.columns.filter((c) => boards.indexOf(c.slice(0, c.indexOf("|"))) >= 0))
    }

    const reload = () => {
      store()
      return load()
    }

    return {
      load,

      loadMore: flow(function* () {
        if (self.loading || self.loadingMore || !self.hasMore) {
          return false
        }

        const no = requestNo
        self.loadingMore = true

        try {
          const page: any = yield self.transport.post<any>(apiUrls.orders.orderQuestion.search, {
            ...self.params,
            skip: self.items.length,
          })

          // пока догружали, поменяли фильтры — эта страница уже не к месту
          if (no !== requestNo) {
            return false
          }

          self.total = page.total || 0
          ;(page.items || []).forEach((item: any) => {
            // строку могли изменить между запросами — второй раз не показываем
            self.items.some((known) => known.id === item.id) || self.items.push(item)
          })

          return true
        } catch (e) {
          self.notify.error(e)
          return false
        } finally {
          self.loadingMore = false
        }
      }),

      /** Фильтры прошлого захода. Ссылка (сотрудник, статус, поиск) ставится поверх */
      restore() {
        const saved = readFilters()

        self.statuses = cast([])
        self.columns = cast([])
        self.due = saved.due ?? ""
        self.orderIds = cast(saved.orderIds ?? [])
        self.authorIds = cast(saved.authorIds ?? [])
        self.employerIds = cast(saved.employerIds ?? [])
        self.implementerIds = cast(saved.implementerIds ?? [])
        self.sort = saved.sort || "deadline"
        self.desc = !!saved.desc
      },

      /**
       * Переход по ссылке: задания сотрудника в нужном статусе или поиск с доски.
       * Остальные фильтры сбрасываем — иначе ссылка покажет не то, что обещала
       */
      applyLink(link: { employerId?: string; employerName?: string; status?: string; query?: string }) {
        // задания сотрудника — со всех досок: столько же, сколько на его бейджах
        self.allBoards = !!link.employerId
        self.boardIds = cast([])
        self.due = ""
        self.orderIds = cast([])
        self.authorIds = cast([])
        self.employerIds = cast([])
        self.implementerIds = cast([])
        self.employerId = link.employerId || ""
        self.employerName = link.employerName || ""
        self.statuses = cast(link.status ? [link.status] : [])
        self.columns = cast([])
        self.query = link.query || ""
      },

      setQuery(value: string) {
        self.query = value
      },

      // таймер зовёт вне действия MST — загрузку запускаем через само действие стора
      onQueryChanged: debounce(() => (self as unknown as { load: () => Promise<boolean> }).load(), 400),

      /** Доска, с которой пришли: переключатель «По всем доскам» при этом не трогаем */
      setHomeBoard(id: string) {
        self.homeBoard = id || ""
      },

      setAllBoards(value: boolean) {
        self.allBoards = value
        dropForeignColumns()
        load()
      },

      /** Выбор досок вручную снимает «По всем доскам»; пустой выбор — снова своя доска */
      setBoards(ids: string[]) {
        self.allBoards = false
        self.boardIds = cast(ids)
        dropForeignColumns()
        load()
      },

      setColumns(ids: string[]) {
        self.columns = cast(ids)
        load()
      },

      /** Снять статус из ссылки: остаются задания сотрудника во всех открытых статусах */
      clearStatuses() {
        self.statuses = cast([])
        load()
      },

      setDue(value: string) {
        self.due = value
        reload()
      },

      setOrderIds(ids: string[]) {
        self.orderIds = cast(ids)
        reload()
      },

      setAuthorIds(ids: string[]) {
        self.authorIds = cast(ids)
        reload()
      },

      setEmployerIds(ids: string[]) {
        self.employerIds = cast(ids)
        reload()
      },

      setImplementerIds(ids: string[]) {
        self.implementerIds = cast(ids)
        reload()
      },

      clearEmployer() {
        self.employerId = ""
        self.employerName = ""
        load()
      },

      /** Сортировка из шапки грида: поле и направление уже выбраны */
      setSortTo(sort: string, desc: boolean) {
        if (self.sort === sort && self.desc === desc) {
          return
        }

        self.sort = sort
        self.desc = desc
        reload()
      },

      setSort(sort: string) {
        // повторный выбор того же поля разворачивает порядок
        if (self.sort === sort) {
          self.desc = !self.desc
        } else {
          self.sort = sort
          self.desc = sort === "created"
        }

        reload()
      },
    }
  })
  .named("QuestionList")

export type QuestionListType = typeof QuestionList.Type
export type QuestionListRowType = typeof QuestionListRow.Type
export type QuestionListBoardType = typeof QuestionListBoard.Type

export const initialListState = (): typeof QuestionList.SnapshotType => ({
  items: [],
  total: 0,
  counts: {},
  columnCounts: {},
  boardCounts: {},
  boards: [],
  query: "",
  homeBoard: "",
  allBoards: false,
  boardIds: [],
  statuses: [],
  columns: [],
  due: "",
  orderIds: [],
  authorIds: [],
  employerIds: [],
  implementerIds: [],
  employerId: "",
  employerName: "",
  sort: "deadline",
  desc: false,
  loading: false,
  loadingMore: false,
  loaded: false,
})
